import { themeAtom } from "../../shared/atoms";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import {
  Bq2CertificationM,
  initialBq2Certification,
  mapSqlToBq2CertificationForm,
} from "../models/bq2-certifications.model";
import { formDate } from "../services/baltic-forms.utils";
import { bqF2CertificationAtom } from "../state/baltic-form.atoms";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface Props {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq2Certification: React.FC<Props> = ({ isCompletedForm, cacheInitialForm, readonly }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF2CertificationAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq2CertificationM>(initialBq2Certification);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const updateDate = (date: Date | null, field: string) => {
    setForm((prevState) => {
      const _form = {
        ...prevState,
        [field]: date !== null ? date.toISOString() : null,
      };

      return _form;
    });
  };

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`${apiUrl}/bq_Certification`, {
        params: {
          imo_no: imoNumber,
          vessel_name: vesselName,
        },
      });
      const _formSql = result.data[0];
      const _form = mapSqlToBq2CertificationForm(_formSql);
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsBg,
        )}
      >
        <h1 className="custom-heading">Certification</h1>

        <form onSubmit={handleSubmit}>
          {/* Safety Equipment Certificate (SEC) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Safety Equipment Certificate (SEC)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                selected={formDate(form.secIssued)}
                onChange={(date) => updateDate(date, "secIssued")}
              />
            </div>

            {/* Last Annual */}
            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="secLastAnnual"
                selected={formDate(form.secLastAnnual)}
                onChange={(date) => updateDate(date, "secLastAnnual")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="secExpires"
                selected={formDate(form.secExpires)}
                onChange={(date) => updateDate(date, "secExpires")}
              />
            </div>
          </div>

          {/* Safety Radio Certificate (SRC) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Safety Radio Certificate (SRC)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="srcIssued"
                selected={formDate(form.srcIssued)}
                onChange={(date) => updateDate(date, "srcIssued")}
              />
            </div>

            {/* Last Annual */}
            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="srcLastAnnual"
                selected={formDate(form.srcLastAnnual)}
                onChange={(date) => updateDate(date, "srcLastAnnual")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="srcExpires"
                selected={formDate(form.srcExpires)}
                onChange={(date) => updateDate(date, "srcExpires")}
              />
            </div>
          </div>

          {/* Safety Construction Certificate (SCC) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Safety Construction Certificate (SCC)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="sccIssued"
                selected={formDate(form.sccIssued)}
                onChange={(date) => updateDate(date, "sccIssued")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="sccLastAnnual"
                selected={formDate(form.sccLastAnnual)}
                onChange={(date) => updateDate(date, "sccLastAnnual")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="sccExpires"
                selected={formDate(form.sccExpires)}
                onChange={(date) => updateDate(date, "sccExpires")}
              />
            </div>
          </div>

          {/* International Loadline Certificate (ILC) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">International Loadline Certificate (ILC)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="ilcIssued"
                selected={formDate(form.ilcIssued)}
                onChange={(date) => updateDate(date, "ilcIssued")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="ilcLastAnnual"
                selected={formDate(form.ilcLastAnnual)}
                onChange={(date) => updateDate(date, "ilcLastAnnual")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="ilcExpires"
                selected={formDate(form.ilcExpires)}
                onChange={(date) => updateDate(date, "ilcExpires")}
              />
            </div>
          </div>

          {/* ISM Safety Management Certificate (SMC) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">ISM Safety Management Certificate (SMC)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="smcIssued"
                selected={formDate(form.smcIssued)}
                onChange={(date) => updateDate(date, "smcIssued")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Last Intermediate</label>
              <Datepicker
                disabled={readonly}
                name="smcLastIntermediate"
                selected={formDate(form.smcLastIntermediate)}
                onChange={(date) => updateDate(date, "smcLastIntermediate")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="smcExpires"
                selected={formDate(form.smcExpires)}
                onChange={(date) => updateDate(date, "smcExpires")}
              />
            </div>
          </div>

          {/* Document of Compliance (DOC) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Document of Compliance (DOC)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="docIssued"
                selected={formDate(form.docIssued)}
                onChange={(date) => updateDate(date, "docIssued")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Endorsed</label>
              <Datepicker
                disabled={readonly}
                name="docEndorsed"
                selected={formDate(form.docEndorsed)}
                onChange={(date) => updateDate(date, "docEndorsed")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="docExpires"
                selected={formDate(form.docExpires)}
                onChange={(date) => updateDate(date, "docExpires")}
              />
            </div>
          </div>

          {/* Cargo Gear (Issued, Last Annual, Expires) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Cargo Gear</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="cargoGearIssued"
                selected={formDate(form.cargoGearIssued)}
                onChange={(date) => updateDate(date, "cargoGearIssued")}
              />
            </div>

            {/* Last Annual */}
            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="cargoGearLastAnnual"
                selected={formDate(form.cargoGearLastAnnual)}
                onChange={(date) => updateDate(date, "cargoGearLastAnnual")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="cargoGearExpires"
                selected={formDate(form.cargoGearExpires)}
                onChange={(date) => updateDate(date, "cargoGearExpires")}
              />
            </div>
          </div>

          {/* Cargo Securing Manual (issued, last annual, expires) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Cargo Securing Manual</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="cargoSecManualIssued"
                selected={formDate(form.cargoSecManualIssued)}
                onChange={(date) => updateDate(date, "cargoSecManualIssued")}
              />
            </div>

            {/* Last Annual */}
            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="cargoSecManualLastAnnual"
                selected={formDate(form.cargoSecManualLastAnnual)}
                onChange={(date) => updateDate(date, "cargoSecManualLastAnnual")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="cargoSecManualExpires"
                selected={formDate(form.cargoSecManualExpires)}
                onChange={(date) => updateDate(date, "cargoSecManualExpires")}
              />
            </div>
          </div>

          {/* International Oil Pollution Prevention Certificate (IOPPC) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">
            International Oil Pollution Prevention Certificate (IOPPC)
          </h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="ioppcIssued"
                selected={formDate(form.ioppcIssued)}
                onChange={(date) => updateDate(date, "ioppcIssued")}
              />
            </div>

            {/* Last Annual */}
            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="ioppcLastAnnual"
                selected={formDate(form.ioppcLastAnnual)}
                onChange={(date) => updateDate(date, "ioppcLastAnnual")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="ioppcExpires"
                selected={formDate(form.ioppcExpires)}
                onChange={(date) => updateDate(date, "ioppcExpires")}
              />
            </div>
          </div>

          {/* Ship Sanitation Control (SSCC)/Ship Sanitation Control Exemption (SSCE) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Ship Sanitation Control (SSCC) / SSCE</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="ssccSsceIssued"
                selected={formDate(form.ssccSsceIssued)}
                onChange={(date) => updateDate(date, "ssccSsceIssued")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="ssccSsceExpires"
                selected={formDate(form.ssccSsceExpires)}
                onChange={(date) => updateDate(date, "ssccSsceExpires")}
              />
            </div>
          </div>

          {/* U.S. Certificate of Financial Responsibility (COFR) (Issued, Expires) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">
            U.S. Certificate of Financial Responsibility (COFR)
          </h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="cofrIssued"
                selected={formDate(form.cofrIssued)}
                onChange={(date) => updateDate(date, "cofrIssued")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="cofrExpires"
                selected={formDate(form.cofrExpires)}
                onChange={(date) => updateDate(date, "cofrExpires")}
              />
            </div>
          </div>

          {/* International Ship Security Certificate (ISSC) (Issued, Expires) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">
            International Ship Security Certificate (ISSC)
          </h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="isscIssued"
                selected={formDate(form.isscIssued)}
                onChange={(date) => updateDate(date, "isscIssued")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="isscExpires"
                selected={formDate(form.isscExpires)}
                onChange={(date) => updateDate(date, "isscExpires")}
              />
            </div>
          </div>

          {/* Maritime Labour Certificate (MLC) (Issued, Expires) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Maritime Labour Certificate (MLC)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="mlcIssued"
                selected={formDate(form.mlcIssued)}
                onChange={(date) => updateDate(date, "mlcIssued")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="mlcExpires"
                selected={formDate(form.mlcExpires)}
                onChange={(date) => updateDate(date, "mlcExpires")}
              />
            </div>
          </div>

          {/* Minimum Safe Manning Certificate (MSM) (Issued, Last Annual, Expires) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Minimum Safe Manning Certificate (MSM)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="msmIssued"
                selected={formDate(form.msmIssued)}
                onChange={(date) => updateDate(date, "msmIssued")}
              />
            </div>

            {/* Last Annual */}
            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="msmLastAnnual"
                selected={formDate(form.msmLastAnnual)}
                onChange={(date) => updateDate(date, "msmLastAnnual")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="msmExpires"
                selected={formDate(form.msmExpires)}
                onChange={(date) => updateDate(date, "msmExpires")}
              />
            </div>
          </div>

          {/* Certificate of Registry (COR) (Issued, Expires) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">Certificate of Registry (COR)</h2>
          <div className="mb-4 grid grid-cols-8 gap-4">
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="corIssued"
                selected={formDate(form.corIssued)}
                onChange={(date) => updateDate(date, "corIssued")}
              />
            </div>

            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly || form.corExpiresNA}
                name="corExpires"
                selected={form.corExpiresNA ? null : formDate(form.corExpires)}
                placeholderText="Expiration date"
                onChange={(date) => updateDate(date, "corExpires")}
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="corExpiresNA" className="text-sm font-bold">
                Expiry Not Applicable
              </label>
              <input
                disabled={readonly}
                className="ml-1 mt-4"
                type="checkbox"
                id="corExpiresNA"
                checked={form.corExpiresNA}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setForm((prev) => ({
                    ...prev,
                    corExpiresNA: checked,
                    corExpires: checked ? null : prev.corExpires,
                  }));
                }}
              />
            </div>
          </div>

          {/* International Tonnage Certificate (ITC) (Issued, Last Annual, Expires) */}
          <h2 className="text-md mb-2 mt-[60px] font-semibold text-green">International Tonnage Certificate (ITC)</h2>

          <div className="mb-4 grid grid-cols-8 gap-4">
            {/* Issued */}
            <div>
              <label className="block text-sm font-bold">Issued</label>
              <Datepicker
                disabled={readonly}
                name="itcIssued"
                selected={formDate(form.itcIssued)}
                onChange={(date) => updateDate(date, "itcIssued")}
              />
            </div>

            {/* Last Annual */}
            <div>
              <label className="block text-sm font-bold">Last Annual</label>
              <Datepicker
                disabled={readonly}
                name="itcLastAnnual"
                selected={formDate(form.itcLastAnnual)}
                onChange={(date) => updateDate(date, "itcLastAnnual")}
              />
            </div>

            {/* Expires */}
            <div>
              <label className="block text-sm font-bold">Expires</label>
              <Datepicker
                disabled={readonly}
                name="itcExpires"
                selected={formDate(form.itcExpires)}
                onChange={(date) => updateDate(date, "itcExpires")}
              />
            </div>
          </div>
          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
